import axios, { AxiosError, CancelTokenSource, ResponseType } from "axios";
import { getTokenUser } from "@/src-refactor/modules/shared/helpers/getUserInfo.helpers";
import ENVIRONMENT_VAR from "@/src-refactor/modules/shared/model/const/env/env.const";
import { stringify } from "flatted";

// Define un tipo para las opciones de la solicitud
type RequestOptions = {
  baseUrl?: string;
  url: string;
  method: string;
  data?: any;
  params?: any;
  body?: any;
  content_type?: contentType;
  Headers?: Record<string, string>;
  responseType?: ResponseType;
};

// Define un tipo para la respuesta exitosa
type SuccessResponse = {
  data: any;
};

// Define un tipo para la respuesta de error
type ErrorResponse = {
  error: {
    status?: number;
    data?: any;
    code?: string;
  };
};

const pendingRequests: Record<string, CancelTokenSource> = {};

export const axiosBaseQuery =
  ({ baseUrl = "" }: { baseUrl?: string } = {}) =>
  async ({
    url,
    method,
    data,
    params,
    body,
    content_type,
    Headers,
    responseType
  }: RequestOptions): Promise<SuccessResponse | ErrorResponse> => {
    const key = stringify({ url, method, data, params, body });

    if (pendingRequests[key]) {
      // Si la solicitud ya existe, cancelarla
      pendingRequests[key].cancel(
        "La solicitud ha sido cancelada debido a una solicitud duplicada"
      );
      delete pendingRequests[key];
    }

    try {
      const source: CancelTokenSource = axios.CancelToken.source();
      pendingRequests[key] = source;

      const response = await axios({
        url: ENVIRONMENT_VAR.API_URL + url,
        method,
        data: body,
        params,
        responseType: responseType,
        headers: {
          "access-control-allow-origin": "*",
          "x-api-key": ENVIRONMENT_VAR.API_KEY,
          "x-requested-with": "XMLHttpRequest",
          Authorization: `Bearer ${getTokenUser()?.token}`,
          "X-AUTH-TOKEN": getTokenUser()?.maxCoreTokenV1,
          "Content-Type": content_type || "multipart/form-data",
          Accept: "*/*",
          ...Headers
        },
        cancelToken: source.token
      });

      delete pendingRequests[key];
      return { data: response.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;

      if (err.code !== "ERR_CANCELED") {
        return {
          error: {
            status: err.response?.status,
            data: err.response?.data || err.message
          }
        };
      } else {
        return {
          error: {
            code: "cancel"
          }
        };
      }
    }
  };

type contentType =
  | "multipart/form-data"
  | "application/json"
  | "application/x-www-form-urlencoded"
  | "text/plain"
  | "text/html"
  | "application/xml"
  | "text/xml"
  | "application/javascript"
  | "text/javascript"
  | "application/json;charset=utf-8"
  | "application/json;charset=UTF-8"
  | "application/json;charset=utf8"
  | "application/json;charset=UTF8"
  | "application/x-www-form-urlencoded;charset=utf-8"
  | "application/x-www-form-urlencoded;charset=UTF-8"
  | "application/x-www-form-urlencoded;charset=utf8"
  | "application/x-www-form-urlencoded;charset=UTF8"
  | "text/plain;charset=utf-8"
  | "text/plain;charset=UTF-8"
  | "text/plain;charset=utf8"
  | "text/plain;charset=UTF8"
  | "text/html;charset=utf-8"
  | "text/html;charset=UTF-8"
  | "text/html;charset=utf8"
  | "text/html;charset=UTF8"
  | "application/xml;charset=utf-8"
  | "application/xml;charset=UTF-8"
  | "application/xml;charset=utf8"
  | "application/xml;charset=UTF8"
  | "text/xml;charset=utf-8"
  | "text/xml;charset=UTF-8"
  | "text/xml;charset=utf8"
  | "text/xml;charset=UTF8"
  | "application/javascript;charset=utf-8"
  | "application/javascript;charset=UTF-8"
  | "application/javascript;charset=utf8"
  | "application/javascript;charset=UTF8"
  | "text/javascript;charset=utf-8"
  | "text/javascript;charset=UTF-8"
  | "text/javascript;charset=utf8"
  | "text/javascript;charset=UTF8";
